import type { Variables } from '@readme/iso/src/types/rdmd';

import { VARIABLE_REGEXP } from '@readme/variable';

const regex = new RegExp(VARIABLE_REGEXP, 'g');

function mergeVariables({ user, defaults }: Variables) {
  const defaultsMap: Record<string, string> = Object.fromEntries(
    defaults.map(({ name, default: def }) => [name, def ?? '']),
  );
  return {
    ...defaultsMap,
    ...user,
  };
}

/**
 * When using the new rmdx renderer to parse `md` format content, we need to
 * pre-process legacy variables. `mdx-js/mdx` uses an internal markdown parser
 * that parses angle brackets (< and >) as HTML by default. This causes the legacy variable
 * syntax <<foo>> to get interpreted as a broken tag before we're able to add custom
 * replacement logic with a remark plugin.
 *
 * `mdx-js/mdx` does not expose a way to write a custom tokenizer for the parser,
 * so we need to pre-process the content to expand legacy variables before the content is
 * parsed with `mdx-js/mdx`.
 */
export default function preprocessLegacyVariables(string: string = '', variables?: Variables) {
  const mergedVariables = variables ? mergeVariables(variables) : [];

  return string.replace(regex, match => {
    const name = match.slice(2, -2).trim();

    // Pass through legacy glossary terms
    if (name.startsWith('glossary:')) {
      return name.replace('glossary:', '');
    }

    // If the variable is not found, return the original match
    return mergedVariables?.[name] ?? match;
  });
}
